const STORAGE_KEY = "forgeswap_tx_history";
const MAX_ENTRIES = 100;

export interface HistoryEntry {
  id: string;
  type: "transfer" | "swap";
  hash: string;
  timestamp: number;
  status: "success";
  chainId: number;
  fromSymbol: string;
  fromAmount: string;
  // Transfer-specific
  recipient?: string;
  // Swap-specific
  toSymbol?: string;
  toAmount?: string;
  toChainId?: number;
}

type NewHistoryEntry = Omit<HistoryEntry, "id" | "timestamp">;

/**
 * Generate a unique ID for history entries.
 * Falls back to timestamp + random if crypto.randomUUID is unavailable.
 */
const generateId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
};

/**
 * Check if localStorage is available and writable.
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = "__ls_test__";
    localStorage.setItem(testKey, "1");
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

/**
 * Push a new history entry to localStorage.
 * Silently no-ops if localStorage is unavailable.
 */
export const pushHistoryEntry = (entry: NewHistoryEntry): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    const fullEntry: HistoryEntry = {
      ...entry,
      id: generateId(),
      timestamp: Date.now(),
    };

    const existing = getHistoryEntries();
    const updated = [fullEntry, ...existing].slice(0, MAX_ENTRIES);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    // Handle quota exceeded by dropping oldest entries
    if (
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" ||
        error.name === "NS_ERROR_DOM_QUOTA_REACHED")
    ) {
      try {
        const existing = getHistoryEntries();
        // Drop half the entries and retry
        const trimmed = existing.slice(0, Math.floor(existing.length / 2));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
        // Retry the push
        pushHistoryEntry(entry);
      } catch {
        // Give up silently
        console.warn("[transaction-history] Failed to save after trimming");
      }
    } else {
      console.warn("[transaction-history] Failed to save entry:", error);
    }
  }
};

/**
 * Get all history entries from localStorage.
 * Returns empty array if unavailable or corrupted.
 */
export const getHistoryEntries = (): HistoryEntry[] => {
  if (!isLocalStorageAvailable()) {
    return [];
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      console.warn("[transaction-history] Invalid data format, resetting");
      return [];
    }
    return parsed as HistoryEntry[];
  } catch (error) {
    console.warn("[transaction-history] Failed to parse history:", error);
    return [];
  }
};

/**
 * Clear all transaction history.
 */
export const clearHistory = (): void => {
  if (!isLocalStorageAvailable()) {
    return;
  }

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn("[transaction-history] Failed to clear history:", error);
  }
};

/**
 * Format a timestamp as relative time (e.g., "2 hours ago").
 */
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) {
    return "Just now";
  }
  if (diffMin < 60) {
    return `${diffMin} ${diffMin === 1 ? "minute" : "minutes"} ago`;
  }
  if (diffHour < 24) {
    return `${diffHour} ${diffHour === 1 ? "hour" : "hours"} ago`;
  }
  if (diffDay < 7) {
    return `${diffDay} ${diffDay === 1 ? "day" : "days"} ago`;
  }

  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Truncate an address to first 6 and last 4 characters.
 */
export const truncateAddress = (address: string): string => {
  if (address.length <= 12) {
    return address;
  }
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};
